import { useSubjectMember } from '@healthblocks-io/core'
import { t } from '@lingui/macro'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { AppHeader } from '@/@bothrs-ui/web/components/navigation/header/AppHeader'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { environment } from '@/config/environment'
import { breakpoints } from '@/config/layout'
import { ContentPageLayout } from '@/layout/components/content-page-layout/ContentPageLayout'
import { useHideNativeNavigation } from '@/layout/providers/LayoutProvider'
import { buildUrlWithSearchParameters } from '@/utils/url'
import { useCareProgramById } from '../../hooks/useCarePrograms'

enum CareProgramEnrollFormStatus {
  ERROR = 'error',
  SUCCESS = 'success',
}

export const CareProgramEnrollForm = () => {
  const [status, setStatus] = useState<CareProgramEnrollFormStatus | undefined>()

  const iframeRef = useRef<HTMLIFrameElement>(null)

  const [searchParameters] = useSearchParams()
  const location = useLocation()

  const subject = useSubjectMember()
  const navigate = useNavigate()

  useHideNativeNavigation()

  const programId = searchParameters.get('programId') ?? undefined

  const { data: careProgram } = useCareProgramById(programId)

  const urlToForm = useMemo(() => {
    let parameters: Record<string, string> = {
      recipients: careProgram?.emailList ?? environment.care.defaultFormRecipient,
    }

    if (careProgram?.id) {
      parameters = {
        ...parameters,
        program: careProgram.id,
      }
    }

    if (subject?.name || (subject?.givenName && subject?.familyName)) {
      parameters = {
        ...parameters,
        name: subject?.name ?? `${subject?.givenName} ${subject?.familyName}`,
      }
    }

    if (subject?.email) {
      parameters = {
        ...parameters,
        email: subject.email,
      }
    }

    if (subject?.phone) {
      parameters = {
        ...parameters,
        phone: subject.phone,
      }
    }

    if (subject?.birthDate) {
      parameters = {
        ...parameters,
        birthdate: subject.birthDate,
      }
    }

    return buildUrlWithSearchParameters(environment.care.enrollFormUrl, parameters)
  }, [careProgram, subject])

  useEffect(() => {
    const handleMessages = (event: MessageEvent) => {
      if (event.data === 'success') {
        setStatus(CareProgramEnrollFormStatus.SUCCESS)
      }
    }

    if (iframeRef.current) {
      window.addEventListener('message', handleMessages)
    }

    // Clean up on unmount
    return () => {
      window.removeEventListener('message', handleMessages)
    }
  }, [location.pathname])

  return (
    <ContentPageLayout
      noScroll
      showHeaderAlways={true}
      header={
        <AppHeader
          primaryAction={{
            label: 'Terug',
            icon: ['far', 'chevron-left'],
            onClick: () => {
              if (status === CareProgramEnrollFormStatus.SUCCESS) {
                navigate(-2)
              } else if (status === CareProgramEnrollFormStatus.ERROR) {
                setStatus(undefined)
              } else {
                navigate(-1)
              }
            },
          }}
        />
      }
    >
      {status ? (
        status === CareProgramEnrollFormStatus.ERROR ? (
          <StStatusContainer>
            <FlexColumn justifyContent='center' alignContent='center' flex={1} style={{ height: '100%' }}>
              <StStatusTitle>
                {t({
                  id: 'careProgramEnrollForm.errorTitle',
                  message: 'Er ging iets mis',
                })}
              </StStatusTitle>
              <StStatusMessage type='BodyMediumRegular'>
                {t({
                  id: 'careProgramEnrollForm.errorMessage',
                  message: 'Er is een fout opgetreden bij het laden van het formulier. Probeer het later opnieuw.',
                })}
              </StStatusMessage>
            </FlexColumn>
          </StStatusContainer>
        ) : status === CareProgramEnrollFormStatus.SUCCESS ? (
          <StStatusContainer>
            <StImage src='/img/svg/care-program-form-submitted.svg' alt='Success' />
            <StStatusTitle>
              {t({
                id: 'careProgramEnrollForm.successTitle',
                message: 'Je aanvraag is verstuurd',
              })}
            </StStatusTitle>
            <StStatusMessage type='BodyMediumRegular'>
              {t({
                id: 'careProgramEnrollForm.successMessage',
                message:
                  'We nemen contact met je op binnen enkele dagen. Klik op de knop hieronder om de pagina te sluiten en terug te keren.',
              })}
            </StStatusMessage>
          </StStatusContainer>
        ) : null
      ) : (
        <StContainer>
          <StTitle>
            {t({
              id: 'careProgramEnrollForm.title',
              message: 'Bedankt voor je interesse',
            })}
          </StTitle>

          <StDescription type='BodyLargeRegular'>
            {t({
              id: 'careProgramEnrollForm.description',
              message: 'Vul je gegevens in.',
            })}
          </StDescription>
          <StIFrame
            ref={iframeRef}
            src={urlToForm}
            title='Mijn Ziekenhuis Form'
            onError={(error) => {
              console.error('Error loading iframe', error)
              setStatus(CareProgramEnrollFormStatus.ERROR)
            }}
          />
        </StContainer>
      )}
    </ContentPageLayout>
  )
}

const StContainer = styled.div`
  flex: 1;
  height: 100%;
`

const StTitle = styled(H3)`
  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space2};
`

const StDescription = styled(P)`
  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StIFrame = styled.iframe`
  height: 100%;
  width: 100%;
  border: none;

  padding-bottom: calc(
    max(var(--safe-area-inset-bottom, 0px), 20px) +
      ${({ theme }) => theme.UI.Spacing.Space1}px
  );
  @media (${breakpoints.fromM}) {
    padding-bottom: calc(
      max(var(--safe-area-inset-bottom, 0px), 20px) +
        ${({ theme }) => theme.UI.Spacing.Space6}px
    );
  }
`

const StStatusContainer = styled(FlexColumn)`
  flex: 1;

  max-width: 600px;
  align-self: center;
`

const StImage = styled.img`
  height: auto;
  margin: 0 ${({ theme }) => theme.UI.SpacingPx.Space6};
  width: 250px;

  align-self: center;

  margin-bottom: ${({ theme }) => theme.UI.SpacingPx.Space4};
`

const StStatusTitle = styled(H3)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

const StStatusMessage = styled(P)`
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  font-weight: 400;
`
